"use client";

import React from "react";

interface Schedule {
  monday: { morning: boolean; afternoon: boolean; evening: boolean };
  tuesday: { morning: boolean; afternoon: boolean; evening: boolean };
  wednesday: { morning: boolean; afternoon: boolean; evening: boolean };
  thursday: { morning: boolean; afternoon: boolean; evening: boolean };
  friday: { morning: boolean; afternoon: boolean; evening: boolean };
  saturday: { morning: boolean; afternoon: boolean; evening: boolean };
  sunday: { morning: boolean; afternoon: boolean; evening: boolean };
}

export interface TherapistScheduleProps {
  schedule: Schedule;
  timezone: string;
  showNationwide?: boolean;
}

export function TherapistSchedule({ schedule, timezone, showNationwide = true }: TherapistScheduleProps) {
  const days = [
    { key: 'monday' as const, label: 'Mon' },
    { key: 'tuesday' as const, label: 'Tue' },
    { key: 'wednesday' as const, label: 'Wed' },
    { key: 'thursday' as const, label: 'Thu' },
    { key: 'friday' as const, label: 'Fri' },
    { key: 'saturday' as const, label: 'Sat' },
    { key: 'sunday' as const, label: 'Sun' }
  ];
  
  const timeSlots = [
    { 
      key: 'morning' as const, 
      label: 'Morning', 
      time: '9am - 12pm'
    },
    { 
      key: 'afternoon' as const, 
      label: 'Afternoon', 
      time: '12pm - 5pm'
    },
    { 
      key: 'evening' as const, 
      label: 'Evening', 
      time: '5pm - 8pm'
    }
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
        <h3 className="font-semibold text-gray-900">When We Can Meet</h3>
        <div className="text-xs sm:text-sm text-gray-600">
          <div className="flex items-center font-medium">
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Times shown in {timezone}</span>
          </div>
          {showNationwide && (
            <p className="text-xs text-gray-500 mt-1 ml-5 sm:ml-6">Available across Canada</p>
          )}
        </div>
      </div>
      
      <div className="space-y-3 sm:space-y-4">
        {/* Days header - Hidden on mobile, shown on tablet+ */}
        <div className="hidden sm:grid grid-cols-8 gap-1 sm:gap-2 mb-2">
          <div></div>
          {days.map((day) => (
            <div key={day.key} className="text-center">
              <span className="text-xs font-medium text-gray-700">{day.label}</span>
            </div>
          ))}
        </div>
        
        {/* Time slots */}
        {timeSlots.map((slot) => (
          <div key={slot.key} className="bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
            {/* Mobile layout - stacked */}
            <div className="sm:hidden">
              <div className="font-medium text-gray-900 text-sm mb-2">{slot.label}</div>
              <div className="text-xs text-gray-600 mb-3">{slot.time}</div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                  const isAvailable = schedule[day.key]?.[slot.key];
                  return (
                    <div key={`${day.key}-${slot.key}`} className="text-center">
                      <div className="text-xs font-medium text-gray-600 mb-1">{day.label}</div>
                      <div className="flex justify-center">
                        {isAvailable ? (
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-primary-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414 1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-8 h-8 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Desktop layout - original */}
            <div className="hidden sm:grid grid-cols-8 gap-2 items-center">
              <div>
                <div className="font-medium text-gray-900 text-sm">{slot.label}</div>
                <div className="text-xs text-gray-600">{slot.time}</div>
              </div>
              
              {days.map((day) => {
                const isAvailable = schedule[day.key]?.[slot.key];
                return (
                  <div key={`${day.key}-${slot.key}`} className="flex justify-center">
                    {isAvailable ? (
                      <button className={`
                        w-10 h-10 rounded-xl flex items-center justify-center transition-all shadow-sm
                        bg-gray-100 text-primary-600 hover:scale-110 hover:shadow-md
                      `}>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414 1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
      </div>
      <p className="text-xs text-gray-600 text-center mt-4 sm:mt-6 font-medium px-2">This is my typical schedule • Let's find a time that works for you!</p>
    </div>
  );
}